import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroContent, statistics } from "../data/mockData";
import { useTranslation } from "react-i18next";
import heroVideo from '../assets/manufacturing1.mp4';

const Hero = () => {
  const { t } = useTranslation();
  const statsRef = useRef([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("in");
        });
      },
      { threshold: 0.2 }
    );
    statsRef.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const CountUp = ({ end, duration }) => {
    const [count, setCount] = React.useState(0);
    React.useEffect(() => {
      let start = 0;
      const steps = duration / 20;
      const increment = end / steps;
      const counter = setInterval(() => {
        start += increment;
        if (start >= end) { clearInterval(counter); setCount(end); }
        else setCount(Math.floor(start));
      }, 20);
      return () => clearInterval(counter);
    }, [end, duration]);
    return <span>{count}</span>;
  };

  return (
    <section className="hero-section">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Playfair+Display:wght@700;800&display=swap');

        @keyframes heroTextIn  { from { opacity: 0; transform: translateY(36px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes heroBtnIn   { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes floatCard   { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
        @keyframes orbDrift    { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(20px,-15px) scale(1.08); } }

        .hero-section {
          font-family: 'Outfit', sans-serif;
          position: relative; min-height: 100vh;
          padding: 120px 0 80px; overflow: hidden;
          display: flex; align-items: center;
        }
        .hero-video {
          position: absolute; inset: 0; width: 100%; height: 100%;
          object-fit: cover; z-index: 0;
          filter: blur(3px); transform: scale(1.06);
        }
        .hero-overlay {
          position: absolute; inset: 0; z-index: 1;
          background: linear-gradient(135deg,
            rgba(13,34,51,0.82) 0%, rgba(13,34,51,0.60) 40%, rgba(13,34,51,0.72) 100%);
        }
        .hero-bg {
          position: absolute; inset: 0; z-index: 2;
          background-image:
            radial-gradient(circle at 15% 60%, rgba(58,143,163,0.18) 0%, transparent 45%),
            radial-gradient(circle at 85% 25%, rgba(90,171,154,0.14) 0%, transparent 45%);
          animation: orbDrift 14s ease-in-out infinite;
          pointer-events: none;
        }

        .hero-inner {
          max-width: 1280px; margin: 0 auto;
          padding: 0 24px; position: relative;
          z-index: 3; width: 100%;
        }
        .hero-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px; align-items: center;
        }

        .hero-eyebrow {
          display: inline-block; font-size: 0.78rem; font-weight: 600;
          letter-spacing: 0.12em; text-transform: uppercase;
          color: #5aab9a; background: rgba(90,171,154,0.15);
          border: 1px solid rgba(90,171,154,0.45);
          padding: 6px 18px; border-radius: 999px; margin-bottom: 18px;
          animation: heroTextIn 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s both;
        }
        .hero-title {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 4.5vw, 4rem); font-weight: 800;
          color: #ffffff; margin: 0 0 20px; line-height: 1.2;
          text-shadow: 0 2px 24px rgba(0,0,0,0.4);
          animation: heroTextIn 0.8s cubic-bezier(0.4,0,0.2,1) 0.2s both;
        }
        .hero-desc {
          font-size: 1rem; color: rgba(255,255,255,0.72);
          line-height: 1.8; margin-bottom: 8px; max-width: 540px;
          text-align: justify;
          animation: heroTextIn 0.8s cubic-bezier(0.4,0,0.2,1) 0.32s both;
        }
        .hero-desc-extra {
          font-size: 1rem; color: rgba(255,255,255,0.72);
          line-height: 1.8; max-width: 540px; text-align: justify;
          overflow: hidden; max-height: 0; opacity: 0;
          transition: max-height 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.4s ease, margin 0.3s;
          margin-bottom: 0;
        }
        .hero-desc-extra.open { max-height: 600px; opacity: 1; margin-bottom: 8px; }
        .hero-read-more {
          background: none; border: none; cursor: pointer;
          font-size: 0.82rem; font-weight: 700; color: #5aab9a;
          letter-spacing: 0.04em; padding: 0; margin-bottom: 22px;
          display: inline-flex; align-items: center; gap: 5px;
          transition: color 0.2s, gap 0.2s; font-family: 'Outfit', sans-serif;
        }
        .hero-read-more:hover { color: #3a8fa3; gap: 8px; }
        .hero-read-more svg { transition: transform 0.35s cubic-bezier(0.4,0,0.2,1); }
        .hero-read-more.open svg { transform: rotate(180deg); }

        .hero-btn {
          display: inline-block;
          background: linear-gradient(135deg, #3a8fa3 0%, #5aab9a 100%);
          color: #fff; font-size: 0.95rem; font-weight: 600;
          padding: 14px 32px; border-radius: 999px;
          transition: all 0.35s cubic-bezier(0.4,0,0.2,1);
          box-shadow: 0 8px 24px rgba(58,143,163,0.35);
          border: none; cursor: pointer;
          animation: heroBtnIn 0.7s cubic-bezier(0.4,0,0.2,1) 0.45s both;
          position: relative; overflow: hidden;
        }
        .hero-btn::after {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%);
          background-size: 200% 100%; background-position: 200% center;
          transition: background-position 0.55s ease;
        }
        .hero-btn:hover {
          background: linear-gradient(135deg, #2d7a8e 0%, #4a9888 100%);
          transform: translateY(-3px);
          box-shadow: 0 14px 32px rgba(58,143,163,0.45);
        }
        .hero-btn:hover::after { background-position: -200% center; }

        .hero-stats-wrapper {
          display: flex; justify-content: flex-end; align-items: center;
        }
        .hero-stats { display: flex; flex-direction: column; gap: 26px; }

        .hero-stat-card {
          background: rgba(255,255,255,0.10);
          backdrop-filter: blur(22px) saturate(1.6);
          -webkit-backdrop-filter: blur(22px) saturate(1.6);
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 20px; padding: 20px 26px; width: 210px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.15);
          text-align: center;
          transition: transform 0.4s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s, border-color 0.3s, background 0.3s;
          transform: translateX(40px); opacity: 0;
          position: relative; overflow: hidden;
        }
        .hero-stat-card::before {
          content: ''; position: absolute; top: 0; left: 20%; right: 20%; height: 2px;
          background: linear-gradient(90deg, transparent, #3a8fa3, #5aab9a, transparent);
          border-radius: 0 0 4px 4px;
        }
        .hero-stat-card.in {
          transform: translateX(0); opacity: 1;
          animation: floatCard 5s ease-in-out infinite;
        }
        .hero-stat-card.in:nth-child(2) { animation-delay: 0.8s; }
        .hero-stat-card.in:nth-child(3) { animation-delay: 1.6s; }
        .hero-stat-card:hover {
          transform: translateY(-6px) scale(1.03) !important;
          background: rgba(255,255,255,0.18);
          box-shadow: 0 20px 48px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.3);
          border-color: rgba(90,171,154,0.5);
          animation-play-state: paused;
        }
        .hero-stat-value { font-size: 1.8rem; font-weight: 800; color: #5aab9a; margin-bottom: 6px; }
        .hero-stat-label { font-size: 0.75rem; color: rgba(255,255,255,0.6); letter-spacing: 0.05em; font-weight: 500; }

        /* ─── TABLET ≤ 900px ─── */
        @media (max-width: 900px) {
          .hero-section { padding: 100px 0 64px; }
          .hero-grid {
            grid-template-columns: 1fr;
            gap: 44px;
            text-align: center;
          }
          .hero-desc, .hero-desc-extra {
            max-width: 100%;
            text-align: center;
            margin-left: auto; margin-right: auto;
          }
          .hero-stats-wrapper { justify-content: center; }
          .hero-stats {
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: center;
            gap: 20px;
          }
          .hero-stat-card {
            width: 170px;
            padding: 18px 20px;
            transform: translateY(28px);
          }
          .hero-stat-card.in {
            transform: translateY(0);
            animation: floatCard 5s ease-in-out infinite;
          }
          .hero-stat-value { font-size: 1.55rem; }
        }

        /* ─── ALL PHONES ≤ 480px (iPhone 14 Pro Max = 430px) ─── */
        @media (max-width: 480px) {
          .hero-section {
            padding: 90px 0 40px;
            min-height: unset;
            align-items: flex-start;
          }
          .hero-inner { padding: 0 22px; }
          .hero-grid {
            grid-template-columns: 1fr;
            gap: 0;
            text-align: left;
          }
          .hero-eyebrow {
            font-size: 0.68rem;
            padding: 5px 12px;
            margin-bottom: 10px;
            display: inline-block;
          }
          .hero-title {
            font-size: 1.9rem;
            line-height: 1.2;
            margin-bottom: 12px;
          }
          .hero-desc, .hero-desc-extra {
            font-size: 0.88rem;
            line-height: 1.7;
            text-align: left;
            max-width: 100%;
            margin-left: 0; margin-right: 0;
          }
          .hero-read-more { margin-bottom: 18px; }
          .hero-btn { font-size: 0.88rem; padding: 11px 24px; }
          /* Hide stats on all phones */
          .hero-stats-wrapper { display: none !important; }
        }

        /* ─── SMALL MOBILE ≤ 380px ─── */
        @media (max-width: 380px) {
          .hero-section { padding: 80px 0 36px; }
          .hero-inner { padding: 0 18px; }
          .hero-title { font-size: 1.7rem; }
          .hero-desc, .hero-desc-extra { font-size: 0.84rem; }
        }
      `}</style>

      <video autoPlay loop muted playsInline className="hero-video">
        <source src={heroVideo} type="video/mp4" />
      </video>
      <div className="hero-overlay" />
      <div className="hero-bg" />

      <div className="hero-inner">
        <div className="hero-grid">

          {/* ── Text ── */}
          <div>
            <p className="hero-eyebrow">{t("hero.subtitle")}</p>
            <h1 className="hero-title">{t("hero.title")}</h1>

            {(() => {
              const fullText = t("hero.description");
              const words = fullText.split(' ');
              const isLong = words.length > 100;
              const preview = isLong ? words.slice(0, 100).join(' ') + '…' : fullText;
              const extra   = isLong ? words.slice(100).join(' ') : '';
              return (
                <>
                  <p className="hero-desc">{preview}</p>
                  {isLong && (
                    <>
                      <p className={`hero-desc-extra ${expanded ? 'open' : ''}`}>{extra}</p>
                      <button
                        className={`hero-read-more ${expanded ? 'open' : ''}`}
                        onClick={() => setExpanded(v => !v)}
                      >
                        {expanded ? 'Read less' : 'Read more'}
                        <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
                          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </>
                  )}
                </>
              );
            })()}

            <Link to={heroContent.ctaLink}>
              <button className="hero-btn">{t("hero.cta")}</button>
            </Link>
          </div>

          {/* ── Stats ── */}
          <div className="hero-stats-wrapper">
            <div className="hero-stats">
              {statistics.map((stat, index) => (
                <div
                  key={index}
                  ref={(el) => (statsRef.current[index] = el)}
                  className="hero-stat-card"
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  <div className="hero-stat-value">
                    <CountUp end={parseInt(stat.value)} duration={2000} />{stat.suffix || "+"}
                  </div>
                  <div className="hero-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;